import workspaceAdminPermissionRegistry from "./workspace-admin-permissions.json";

const workspacePermissionDependencies = new Map(
    workspaceAdminPermissionRegistry.groups
        .flatMap((group) => group.permissions)
        .map((permission) => [permission.key, permission.dependencies || []]),
);

export function normalizePermissionList(value) {
    let permissions = value;

    if (typeof permissions === "string") {
        try {
            permissions = JSON.parse(permissions);
        } catch {
            permissions = [permissions];
        }
    }

    if (permissions && !Array.isArray(permissions) && typeof permissions === "object") {
        permissions = Object.entries(permissions)
            .filter(([, enabled]) => enabled === true)
            .map(([permission]) => permission);
    }

    return Array.isArray(permissions)
        ? permissions.map((permission) => String(permission || "").trim()).filter(Boolean)
        : [];
}

export function expandWorkspacePermissionDependencies(value) {
    const permissions = new Set(normalizePermissionList(value));
    let changed = true;
    while (changed) {
        changed = false;
        for (const permission of [...permissions]) {
            for (const dependency of workspacePermissionDependencies.get(permission) || []) {
                if (permissions.has(dependency)) continue;
                permissions.add(dependency);
                changed = true;
            }
        }
    }
    return [...permissions];
}

export function getWorkspaceRole(userRoles = []) {
    const workspaceRoles = userRoles.filter((role) => role?.scope_type === "workspace");
    return workspaceRoles.find((role) => role?.role === "owner")
        || workspaceRoles.find((role) => role?.role === "admin")
        || workspaceRoles.find((role) => role?.role === "viewer")
        || workspaceRoles.find((role) => role?.role === "guest")
        || null;
}

export function isWorkspaceOwner(value) {
    if (typeof value === "string") return value === "owner";
    if (value?.role) {
        return value.role === "owner"
            && (!value.scope_type || value.scope_type === "workspace");
    }
    return getWorkspaceRole(value?.user_roles || [])?.role === "owner";
}

export function resolveActiveWorkspaceUserRoles(
    workspaceMembership,
    graphMembership,
    graphUserRoles = [],
) {
    const workspaceMembershipKey = workspaceMembership?.documentId ?? workspaceMembership?.id;
    const graphMembershipKey = graphMembership?.documentId ?? graphMembership?.id;
    const graphMatchesActiveMembership = workspaceMembershipKey !== null
        && workspaceMembershipKey !== undefined
        && graphMembershipKey !== null
        && graphMembershipKey !== undefined
        && String(workspaceMembershipKey) === String(graphMembershipKey);

    const persistedRoles = workspaceMembership?.user_roles || [];
    if (!graphMatchesActiveMembership || !Array.isArray(graphUserRoles) || !graphUserRoles.length) {
        return persistedRoles;
    }

    const roleIdentity = (role) => {
        const persistedKey = role?.documentId || role?.id;
        return persistedKey
            ? `role:${persistedKey}`
            : `scope:${role?.scope_type || ""}:${role?.scope_id || ""}`;
    };
    const persistedByIdentity = new Map(persistedRoles.map((role) => [roleIdentity(role), role]));
    const merged = graphUserRoles.map((graphRole) => {
        const persistedRole = persistedByIdentity.get(roleIdentity(graphRole));
        if (!persistedRole) return graphRole;
        persistedByIdentity.delete(roleIdentity(graphRole));
        return {
            ...persistedRole,
            ...graphRole,
            permissions: graphRole?.permissions
                ?? (graphRole?.role === persistedRole?.role ? persistedRole?.permissions : undefined),
        };
    });

    return [...merged, ...persistedByIdentity.values()];
}

export function isWorkspaceReadOnlyRole(role) {
    const roleName = typeof role === "string" ? role : role?.role;
    return roleName === "viewer" || roleName === "guest";
}

export function shouldBlockWorkspaceMutation(role) {
    // The client-side guard is only a UX optimization for known read-only roles.
    // When role data is missing or still being refreshed, let the backend remain
    // the authorization boundary instead of producing a false local denial.
    return isWorkspaceReadOnlyRole(role);
}

export function canManageWorkspaceSettings(role) {
    const roleName = typeof role === "string" ? role : role?.role;
    return roleName === "owner" || roleName === "admin";
}

const WORKSPACE_CONTEXT_INDEPENDENT_PATHS = new Set([
    "/workspace/create",
]);

export function isWorkspaceContextIndependentPath(pathname) {
    const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
    return WORKSPACE_CONTEXT_INDEPENDENT_PATHS.has(normalized);
}

export function canAccessWorkspacePage(role, page) {
    const roleName = typeof role === "string" ? role : role?.role;
    if (!roleName || !page) return false;

    if (isWorkspaceContextIndependentPath(page["nav-url"])) return true;

    if (roleName === "guest") {
        return page.allowGuest === true;
    }

    if (page.name === "settings" || page["nav-url"] === "/settings") {
        return canManageWorkspaceSettings(roleName);
    }

    if (!["owner", "admin", "viewer"].includes(roleName)) return false;
    if (!page.can || roleName === "owner" || roleName === "viewer") return true;
    if (roleName !== "admin") return false;

    const permissions = normalizePermissionList(role?.permissions);
    return permissions.includes("*")
        || permissions.includes(page.can)
        || permissions.includes(`${String(page.can).split(".")[0]}.*`);
}

export function getEffectivePermissions(
    userRoles = [],
    scopeId,
    scopeRolePermissions = {},
    workspaceStatus = "active",
    scopeType,
) {
    const workspaceRole = getWorkspaceRole(userRoles);
    if (workspaceStatus !== "active") return [];
    if (workspaceRole?.role === "owner") return ["*"];
    if (isWorkspaceReadOnlyRole(workspaceRole)) return [];

    const permissions = new Set(expandWorkspacePermissionDependencies(workspaceRole?.permissions));
    if (scopeId && scopeType) {
        const scopeRole = userRoles.find((role) => (
            role?.scope_type === scopeType
            && String(role?.scope_id) === String(scopeId)
        ));
        const configuredPermissions = scopeRolePermissions?.[scopeRole?.role];
        const scopedPermissions = configuredPermissions === undefined
            ? scopeRole?.permissions
            : configuredPermissions;
        for (const permission of normalizePermissionList(scopedPermissions)) {
            permissions.add(permission);
        }
    }

    return [...permissions];
}
